/* eslint-disable react/jsx-no-bind */
/**
 * @file 项目信息栏（封面 + 名称 + 余额/消耗 + 提取按钮）
 */
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import styles from './ProjectInfoBar.module.less';
import Icon from '@/components/common/Icon';
import Select from '@/components/common/Select';
import type {SelectOption} from '@/components/common/Select';
import type {ProjectPointsInfo} from '@/services/points/interface';
import {createCx} from '@/utils/cx';
import type {ProjectId} from '@/types';
import Tooltip from '@/components/common/Tooltip';
import Popover from '@/components/common/Popover';
import Button from '@/components/common/Button';
import {useTracking} from '@/services/tracking/context';
import {TrackEvents} from '@/services/tracking/constant';

const cx = createCx(styles);

interface ProjectSelectorConfig {
    projects: ProjectPointsInfo[];
    selectedProjectId: ProjectId;
    onProjectChange: (projectId: ProjectId) => void;
}

interface ProjectInfoBarProps {
    project: ProjectPointsInfo;
    projectSelector?: ProjectSelectorConfig;
    isRedeeming?: boolean;
    onRedeem?: () => void;
    className?: string;
}

type ProjectSelectOption = SelectOption &
    Pick<
        ProjectPointsInfo,
        | 'projectName'
        | 'projectCover'
        | 'projectTotalRemaining'
        | 'projectTotalConsumed'
        | 'isDeleted'
    >;

const labelRender = () => <span className={styles.selectLabel}>切换</span>;

// 默认项目封面图
const DEFAULT_COVER_URL =
    'https://now.bdstatic.com/stash/v1/01e9009/mangaflow-fe/9896fde/首页/empty.png';

const formatConsumedPoints = (points: number) => (points > 0 ? `-${points}` : points);

const ProjectInfoBar: React.FC<ProjectInfoBarProps> = ({
    project,
    projectSelector,
    isRedeeming,
    onRedeem,
    className,
}) => {
    const [redeemPopoverOpen, setRedeemPopoverOpen] = useState(false);
    const [redeemTooltipOpen, setRedeemTooltipOpen] = useState(false);
    const suppressRedeemTooltipRef = useRef(false);

    const {sendShowLog, sendClickLog} = useTracking();
    const from = projectSelector ? 'home' : 'workspace';
    const trackExt = useMemo(
        () => ({
            projectName: project.projectName,
            // eslint-disable-next-line camelcase
            project_id: project.projectId,
        }),
        [project.projectId, project.projectName],
    );
    const buildTrackParams = useCallback(
        (value: string) => ({
            page: TrackEvents.PAGE_HOME,
            from,
            value,
            ext: trackExt,
        }),
        [from, trackExt],
    );

    useEffect(() => {
        if (project.isAllowRedeem) {
            sendShowLog(buildTrackParams(TrackEvents.CREDIT_WINDOW_WITHDRAW_BTN));
        }
    }, [buildTrackParams, project.isAllowRedeem, sendShowLog]);

    useEffect(() => {
        if (redeemPopoverOpen) {
            setRedeemTooltipOpen(false);
        } else {
            suppressRedeemTooltipRef.current = false;
        }
    }, [redeemPopoverOpen]);

    const handleRedeemTooltipOpenChange = useCallback(
        (open: boolean) => {
            if (redeemPopoverOpen) {
                setRedeemTooltipOpen(false);
                return;
            }
            if (open && suppressRedeemTooltipRef.current) {
                return;
            }
            setRedeemTooltipOpen(open);
        },
        [redeemPopoverOpen],
    );
    const options: ProjectSelectOption[] | undefined = projectSelector?.projects.map((p) => ({
        value: p.projectId,
        label: p.projectName,
        ...p,
    }));

    const optionMap = useMemo(
        () => new Map(options?.map((opt) => [opt.value, opt]) ?? []),
        [options],
    );

    const selectedIdStr = projectSelector ? projectSelector.selectedProjectId : undefined;

    const optionRender = useCallback(
        (option: {value?: string | number; label?: React.ReactNode}) => {
            const key = String(option.value);
            const opt = optionMap.get(key);
            if (!opt) {
                return option.label;
            }

            const isSelected = selectedIdStr !== undefined && key === selectedIdStr;
            const {
                projectName,
                projectCover,
                projectTotalRemaining,
                projectTotalConsumed,
                isDeleted,
            } = opt;

            const content = (
                <div className={styles.optionContent}>
                    <div className={styles.leftBox}>
                        <div className={styles.coverBox}>
                            <img
                                className={styles.cover}
                                src={projectCover || DEFAULT_COVER_URL}
                                alt="项目封面"
                            />
                        </div>
                        <div className={styles.info}>
                            <div className={styles.nameRow}>
                                <span className={styles.name}>{projectName}</span>
                                {isDeleted && <span className={styles.deletedTag}>已删除</span>}
                            </div>
                            <div className={styles.statsRow}>
                                <div className={styles.stat}>
                                    <div className={cx(styles.textCommon, styles.label)}>余额</div>
                                    <div className={cx(styles.textCommon, styles.value)}>
                                        {projectTotalRemaining}
                                    </div>
                                </div>
                                <div className={styles.stat}>
                                    <div className={cx(styles.textCommon, styles.label)}>
                                        已消耗
                                    </div>
                                    <div className={cx(styles.textCommon, styles.value)}>
                                        {formatConsumedPoints(projectTotalConsumed)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {isSelected && (
                        <span className={styles.optionCheck}>
                            <Icon name="AijhBasicDuihao" size={18} />
                        </span>
                    )}
                </div>
            );

            return isDeleted ? (
                <Tooltip
                    title="当前项目已被删除，点击可查看明细"
                    placement="left"
                    align={{offset: [-10, 0]}}
                    classNames={{
                        root: styles.deletedTooltipRoot,
                        container: styles.deletedTooltip,
                    }}
                >
                    {content}
                </Tooltip>
            ) : (
                content
            );
        },
        [optionMap, selectedIdStr],
    );

    return (
        <div className={cx(styles.container, className)}>
            <div className={styles.leftBox}>
                <div className={styles.coverBox}>
                    <img
                        className={styles.cover}
                        src={project.projectCover || DEFAULT_COVER_URL}
                        alt="项目封面"
                    />
                </div>
                <div className={styles.info}>
                    <div className={styles.nameRow}>
                        <span className={styles.projectName}>{project.projectName}</span>
                        {project.isDeleted && <span className={styles.deletedTag}>已删除</span>}
                    </div>
                    <div className={styles.statsRow}>
                        <div className={styles.stat}>
                            <div className={cx(styles.textCommon, styles.label)}>余额</div>
                            <div className={cx(styles.textCommon, styles.value)}>
                                {project.projectTotalRemaining}
                            </div>
                            {project.isAllowRedeem && (
                                <Popover
                                    classNames={{root: styles.redeemPopover}}
                                    open={redeemPopoverOpen}
                                    onOpenChange={setRedeemPopoverOpen}
                                    trigger="click"
                                    placement="bottom"
                                    title={`确认要提取 ${project.projectTotalRemaining} 积分吗？`}
                                    footer={
                                        <>
                                            <Button
                                                size="mini"
                                                onClick={() => setRedeemPopoverOpen(false)}
                                            >
                                                关闭
                                            </Button>
                                            <Button
                                                size="mini"
                                                type="primary"
                                                loading={isRedeeming}
                                                onClick={() => {
                                                    onRedeem?.();
                                                    setRedeemPopoverOpen(false);
                                                }}
                                            >
                                                确认
                                            </Button>
                                        </>
                                    }
                                >
                                    <Tooltip
                                        title="提取后项目积分回归个人账户"
                                        placement="bottom"
                                        open={redeemTooltipOpen && !redeemPopoverOpen}
                                        onOpenChange={handleRedeemTooltipOpenChange}
                                    >
                                        <div
                                            className={cx(styles.textCommon, styles.redeem, {
                                                [styles.redeemDisabled]:
                                                    project.projectTotalRemaining === 0,
                                            })}
                                            onClick={() => {
                                                sendClickLog(
                                                    buildTrackParams(
                                                        TrackEvents.CREDIT_WINDOW_WITHDRAW_BTN,
                                                    ),
                                                );
                                            }}
                                            onMouseDown={() => {
                                                suppressRedeemTooltipRef.current = true;
                                                setRedeemTooltipOpen(false);
                                            }}
                                            onMouseLeave={() => {
                                                suppressRedeemTooltipRef.current = false;
                                                setRedeemTooltipOpen(false);
                                            }}
                                        >
                                            <Icon
                                                name="AijBasicShangchuan"
                                                size={12}
                                                className={styles.redeemIcon}
                                            />
                                            提取
                                        </div>
                                    </Tooltip>
                                </Popover>
                            )}
                        </div>
                        <div className={styles.stat}>
                            <div className={cx(styles.textCommon, styles.label)}>已消耗</div>
                            <div className={cx(styles.textCommon, styles.value)}>
                                {formatConsumedPoints(project.projectTotalConsumed)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {projectSelector && options && options.length > 1 && (
                <Select
                    classNames={{
                        root: styles.select,
                        popup: {
                            root: styles.selectPopup,
                            listItem: styles.selectListItem,
                        },
                    }}
                    options={options}
                    value={selectedIdStr}
                    onChange={(val) => projectSelector.onProjectChange(val)}
                    onOpenChange={(open) => {
                        if (open) {
                            sendClickLog(buildTrackParams(TrackEvents.CREDIT_WINDOW_SWITCH_BTN));
                        }
                    }}
                    labelRender={labelRender}
                    optionRender={optionRender}
                    placement="bottomRight"
                    suffixIcon={
                        <Icon name="AijBasicXiala" size={14} className={cx('chevronIcon')} />
                    }
                    popupMatchSelectWidth={323}
                />
            )}
        </div>
    );
};

export default ProjectInfoBar;
